// The account's install state (INSTALL.md §4.4): `GET` and `POST /api/me/install`.

import { useCallback, useEffect, useState } from 'react';
import { InstallView, type InstallViewShape } from '@fsp/domain';
import { get, send } from './api';
import { useSession } from './session';

/** Tell the account. Never fatal: an offer made once more is the worst of it. */
export async function reportInstall(
  event: 'installed' | 'offer-closed',
): Promise<InstallViewShape | null> {
  const reply = await send('POST', '/me/install', { event }, InstallView);
  return reply.status === 'ok' ? reply.data : null;
}

/**
 * The signed-in person's install state, asked once when `ask` is true; null
 * until the answer, and for nobody signed in. A failed ask is null too: no
 * answer means no offer, which is the quiet way for this to fail.
 */
export function useInstallState(ask: boolean): {
  state: InstallViewShape | null;
  closeOffer: () => Promise<void>;
} {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const [state, setState] = useState<{ userId: string; view: InstallViewShape } | null>(null);

  useEffect(() => {
    if (!ask || userId === null) return;
    let live = true;
    void get('/me/install', InstallView).then((r) => {
      if (live && r.status === 'ok') setState({ userId, view: r.data });
    });
    return () => {
      live = false;
    };
  }, [ask, userId]);

  const closeOffer = useCallback(async () => {
    if (userId === null) return;
    // Gone from the screen at once; the account's answer follows.
    setState((s) => (s === null ? s : { ...s, view: { ...s.view, offerNow: false } }));
    const view = await reportInstall('offer-closed');
    if (view !== null) setState({ userId, view });
  }, [userId]);

  return { state: state !== null && state.userId === userId ? state.view : null, closeOffer };
}
